import React, { useState } from "react";
import { useCSVReader } from "react-papaparse";

import { useMutation } from "@tanstack/react-query";
import { chunk } from "lodash";
import partition from "lodash/partition";

import { verifyFieldModuleElements, verifyFields } from "services";

import { VERIFY_FIELDS, VERIFY_FIELD_MODULE_ELEMENTS } from "consts";

import { CSV, GetRootProps } from "models";

import { Button, Col, Icon, MainPage, ProgressBar, Row, Tooltip, Typography, Message as message } from "components";

const { Text } = Typography;

type VerificationState = "verified" | "declined" | "pending";

export type FieldVerificationUpdate = {
  fieldVerificationId: string | null;
  fieldVerificationState: VerificationState | null;
};

export type ElementVerificationUpdate = {
  elementVerificationId: string | null;
  elementVerificationState: VerificationState | null;
};

export type FieldAndElementVerification = FieldVerificationUpdate & ElementVerificationUpdate;

type FieldAndElementVerificationUnparsed = Record<keyof FieldAndElementVerification, string | null>;

const chunkSize = 200;

const validateVerifications = (verifications: FieldAndElementVerificationUnparsed[]): verifications is FieldAndElementVerification[] =>
  verifications.every((verification) => {
    const possibleFieldVerificationStates = ["verified", "declined", "pending"];
    const { fieldVerificationId, elementVerificationId, fieldVerificationState, elementVerificationState } = verification;

    return (
      (elementVerificationId === null || fieldVerificationId === null) &&
      !(elementVerificationId === null && fieldVerificationId === null) &&
      (typeof fieldVerificationId === "string" || fieldVerificationId === null) &&
      (typeof elementVerificationId === "string" || elementVerificationId === null) &&
      (fieldVerificationState === null || possibleFieldVerificationStates.includes(fieldVerificationState)) &&
      (elementVerificationState === null || possibleFieldVerificationStates.includes(elementVerificationState))
    );
  });

export const CsvUpload = () => {
  const [saving, setSaving] = useState(false);
  const [processedVerificationCount, setProcessedVerificationCount] = useState(0);
  const [verificationsToProcessCount, setVerificationsToProcessCount] = useState(0);
  const { CSVReader } = useCSVReader();

  const { mutateAsync: mutateFields } = useMutation({
    mutationKey: [VERIFY_FIELDS],
    mutationFn: verifyFields,
  });

  const { mutateAsync: mutateElements } = useMutation({
    mutationKey: [VERIFY_FIELD_MODULE_ELEMENTS],
    mutationFn: verifyFieldModuleElements,
  });

  const processFile = async (csv: CSV) => {
    setSaving(true);

    const { data } = csv;
    const [headers, ...rows] = data;

    const fieldVerificationIdIndex = headers.findIndex((header: string) => header === "fieldVerificationId");
    const fieldVerificationStateIndex = headers.findIndex((header: string) => header === "fieldVerificationState");
    const elementVerificationIdIndex = headers.findIndex((header: string) => header === "elementVerificationId");
    const elementVerificationStateIndex = headers.findIndex((header: string) => header === "elementVerificationState");

    const verifications: FieldAndElementVerificationUnparsed[] = rows.map((row: string[]) => ({
      // If empty string we want to set the ids to null to be more explicit
      fieldVerificationId: row[fieldVerificationIdIndex] || null,
      fieldVerificationState: row[fieldVerificationStateIndex] || null,
      elementVerificationId: row[elementVerificationIdIndex] || null,
      elementVerificationState: row[elementVerificationStateIndex] || null,
    }));

    if (!validateVerifications(verifications)) {
      setSaving(false);

      message({
        content: "The csv does not have the correct format",
        icon: "gremlin",
      });

      return;
    }

    const [fieldVerifications, elementVerifications]: [FieldVerificationUpdate[], ElementVerificationUpdate[]] = partition(
      verifications,
      ({ fieldVerificationId }) => fieldVerificationId !== null
    );

    setVerificationsToProcessCount(verifications.length);

    if (fieldVerifications.length > 0) {
      const chunks = chunk(fieldVerifications, chunkSize);

      // Not using promise all here to avoid race conditions
      for (const chunk of chunks) {
        // eslint-disable-next-line no-await-in-loop
        await mutateFields(chunk);
        setProcessedVerificationCount((count) => count + chunk.length);
      }

      message({
        content: `The gremlins have successfully processed ${fieldVerifications.length} fields`,
        icon: "gremlin",
      });
    }

    if (elementVerifications.length > 0) {
      const chunks = chunk(elementVerifications, chunkSize);

      // Not using promise all here to avoid race conditions
      for (const chunk of chunks) {
        // eslint-disable-next-line no-await-in-loop
        await mutateElements(chunk);
        setProcessedVerificationCount((count) => count + chunk.length);
      }

      message({
        content: `The gremlins have successfully processed ${elementVerifications.length} elements`,
        icon: "gremlin",
      });
    }

    setProcessedVerificationCount(0);
    setSaving(false);
  };

  return (
    <MainPage pageName="admin">
      <Row>
        <Col>
          <h1>Upload verification states</h1>
          <p>
            Upload verification states of fields and elements. You can include fields and elements in one file. The csv format should be as follows:
            <br />
            <Text code>fieldVerificationId,fieldVerificationState,elementVerificationId,elementVerificationState</Text>
            <br />
            <Text code>8766e145-b3aa-4766-b3c2-92b6ed61f7c3,verified|declined|pending,,</Text>
            <br />
            <Text code>,,1f57335a-97dd-4e20-bc93-2268aeddc7c2,verified|declined|pending</Text>
          </p>
        </Col>
      </Row>

      <Row>
        <Col md={8}>
          <Tooltip placement="bottom" title="Upload property list">
            <CSVReader
              config={{
                skipEmptyLines: true,
              }}
              noDrag
              style={{
                dropArea: {
                  border: 0,
                  padding: 0,
                  margin: 0,
                  width: "auto",
                  height: "auto",
                  display: "inline-block",
                },
                progressBar: {
                  display: "none !important",
                  opacity: 0,
                },
              }}
              onUploadAccepted={(v: CSV) => processFile(v)}
            >
              {({ getRootProps }: { getRootProps: GetRootProps }) => (
                <Button
                  className="margin-left-50"
                  disabled={saving}
                  dataTestId="module-access-upload-button"
                  loading={saving}
                  size="small"
                  type="secondary"
                  {...getRootProps()}
                >
                  <Icon iconName="tabler/upload" size="s" />
                </Button>
              )}
            </CSVReader>
          </Tooltip>

          {saving && (
            <>
              <span className="margin-left-300">
                {processedVerificationCount}/{verificationsToProcessCount} verifications processed
              </span>

              <ProgressBar size="small" percent={Math.ceil((processedVerificationCount / verificationsToProcessCount) * 100)} />
            </>
          )}
        </Col>
      </Row>
    </MainPage>
  );
};
